/* Bottles Pro: sticky header state, mobile drawer, scroll reveal and product rails. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isRTL = () => root.dir === 'rtl';

  /* ---------- Header ---------- */
  const header = document.querySelector('[data-bl-header]');
  let announcementHeight = 0;
  let ticking = false;

  const measureHeader = () => {
    if (!header) return;
    announcementHeight = 0;
    header.querySelectorAll('.bl-announcement').forEach((node) => {
      if (!node.hidden && node.offsetHeight) announcementHeight = node.offsetHeight;
    });
    const nav = header.querySelector('.bl-nav');
    root.style.setProperty('--bl-ann-h', `${announcementHeight}px`);
    root.style.setProperty('--bl-header-h', `${header.offsetHeight}px`);
    if (nav) document.body.style.setProperty('--header-height', `${nav.offsetHeight}px`);
  };

  const updateScrolled = () => {
    ticking = false;
    if (header) header.classList.toggle('is-scrolled', window.scrollY > announcementHeight + 4);
  };

  if (header) {
    measureHeader();
    updateScrolled();
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateScrolled);
      },
      { passive: true }
    );
    if ('ResizeObserver' in window) {
      new ResizeObserver(measureHeader).observe(header);
    } else {
      window.addEventListener('resize', measureHeader);
    }
  }

  /* ---------- Mobile drawer ---------- */
  const menu = document.querySelector('[data-bl-menu]');
  if (menu) {
    const summary = menu.querySelector('summary');
    menu.addEventListener('toggle', () => {
      root.classList.toggle('bl-lock', menu.open);
      if (summary) summary.setAttribute('aria-expanded', String(menu.open));
    });
    menu.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-bl-menu-close]')) {
        menu.open = false;
        return;
      }
      const link = event.target.closest('.bl-mobile-panel a[href]');
      if (link && link.getAttribute('href').includes('#')) menu.open = false;
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !menu.open) return;
      menu.open = false;
      summary?.focus();
    });
    const desktop = window.matchMedia('(min-width: 1051px)');
    const closeOnDesktop = (event) => {
      if (event.matches) menu.open = false;
    };
    if (desktop.addEventListener) desktop.addEventListener('change', closeOnDesktop);
  }

  /* ---------- Desktop dropdowns ---------- */
  const dropdowns = document.querySelectorAll('[data-bl-dropdown]');
  if (dropdowns.length) {
    document.addEventListener('click', (event) => {
      dropdowns.forEach((dropdown) => {
        if (dropdown.open && !dropdown.contains(event.target)) dropdown.open = false;
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      dropdowns.forEach((dropdown) => {
        if (!dropdown.open) return;
        dropdown.open = false;
        dropdown.querySelector('summary')?.focus();
      });
    });
  }

  /* ---------- Scroll reveal ---------- */
  const initReveal = (scope = document) => {
    const items = [...scope.querySelectorAll('.bl-rise:not(.is-visible)')];
    if (!items.length) return;
    const canAnimate =
      'IntersectionObserver' in window && !reduceMotion.matches && !root.classList.contains('shopify-design-mode');
    if (!canAnimate) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const viewport = window.innerHeight;
    const pending = [];
    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top < viewport * 0.92 && rect.bottom > 0) {
        item.classList.add('is-visible');
      } else {
        pending.push(item);
      }
    });
    pending.forEach((item) => {
      const siblings = item.parentElement
        ? [...item.parentElement.children].filter((node) => node.classList.contains('bl-rise'))
        : [];
      const index = siblings.indexOf(item);
      if (index > 0) item.style.setProperty('--bl-d', `${(index % 4) * 90}ms`);
    });
    root.classList.add('bl-rise-on');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const item = entry.target;
          item.classList.add('is-visible');
          observer.unobserve(item);
          window.setTimeout(() => item.style.removeProperty('--bl-d'), 1600);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
    );
    pending.forEach((item) => observer.observe(item));
  };

  /* ---------- Rails ---------- */
  const initRails = (scope = document) => {
    scope.querySelectorAll('[data-bl-rail-scope]').forEach((wrap) => {
      if (wrap.dataset.blRailReady === 'true') return;
      const rail = wrap.querySelector('[data-bl-rail]');
      if (!rail) return;
      wrap.dataset.blRailReady = 'true';
      const prev = wrap.querySelector('[data-bl-rail-prev]');
      const next = wrap.querySelector('[data-bl-rail-next]');
      const bar = wrap.querySelector('[data-bl-rail-bar]');

      const step = () => {
        const card = rail.firstElementChild;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 14;
        return card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      };

      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        const position = Math.abs(rail.scrollLeft);
        const ratio = max > 0 ? position / max : 1;
        if (bar) bar.style.setProperty('--p', `${Math.max(14, ratio * 100)}%`);
        if (prev) prev.disabled = position <= 2;
        if (next) next.disabled = position >= max - 2;
      };

      const move = (direction) => {
        const sign = isRTL() ? -1 : 1;
        rail.scrollBy({ left: direction * sign * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      };

      prev?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const init = (scope = document) => {
    initReveal(scope);
    initRails(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) init(event.target);
  });
})();
